import React, { useEffect, useRef, useState } from 'react';
import { CalendarIcon } from 'lucide-react';

export const AutoResizeTextarea = ({ value, onChange, placeholder, disabled, maxLength, className, style, rows }) => {
  const textareaRef = useRef(null);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [value]);

  return (
    <textarea
      ref={textareaRef}
      className={className}
      disabled={disabled}
      rows={rows || 1}
      style={{ ...style, overflow: 'hidden', resize: 'none' }}
      value={value}
      onChange={(e) => {
        e.target.style.height = 'auto';
        e.target.style.height = `${e.target.scrollHeight}px`;
        if (onChange) onChange(e);
      }}
      maxLength={maxLength}
      placeholder={placeholder}
    />
  );
};

export const FormattedNumberInput = ({ value, onChange, onBlur, className, readOnly, ...props }) => {
  const [isFocused, setIsFocused] = useState(false);
  const [localValue, setLocalValue] = useState('');

  const formatWithCommas = (val) => {
    if (val === '' || val === null || val === undefined) return '';
    const parts = val.toString().split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
  };

  useEffect(() => {
    if (!isFocused) {
      setLocalValue(formatWithCommas(value));
    }
  }, [value, isFocused]);

  const format = (num) => {
    if (num === '' || num === null || num === undefined) return '';
    const parsed = parseFloat(num);
    if (Number.isNaN(parsed)) return '';
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(parsed);
  };

  return (
    <input
      {...props}
      type="text"
      className={className}
      readOnly={readOnly}
      value={isFocused ? localValue : format(value)}
      onFocus={(e) => {
        if (!readOnly) {
          setIsFocused(true);
          setLocalValue(value === 0 ? '' : formatWithCommas(value));
        }
        if (props.onFocus) props.onFocus(e);
      }}
      onBlur={(e) => {
        setIsFocused(false);
        if (onBlur) onBlur(e);
      }}
      onChange={(e) => {
        const raw = e.target.value.replace(/,/g, '');
        if (/^\d*\.?\d*$/.test(raw)) {
          setLocalValue(formatWithCommas(raw));
          if (onChange) onChange(raw);
        }
      }}
    />
  );
};

export const SmartDateInput = ({ value, onChange, disabled, placeholder }) => {
  const [focused, setFocused] = useState(false);
  const showDate = focused || !!value;

  return (
    <div className="relative flex h-full w-full items-center justify-center">
      <input
        type={showDate ? 'date' : 'text'}
        className="h-full w-full cursor-pointer bg-transparent px-1 py-2 text-center text-xs focus:bg-blue-50 focus:outline-none dark:text-black"
        disabled={disabled}
        value={value || ''}
        placeholder={placeholder}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onChange={onChange}
      />
      {!value && !focused && (
        <CalendarIcon className="pointer-events-none absolute right-2 top-1/2 h-3 w-3 -translate-y-1/2 transform text-gray-400" />
      )}
    </div>
  );
};
